import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Verified as VerifiedIcon,
  Search as SearchIcon,
  Science as ScienceIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const Sidebar = ({
  isMobile,
  onClose,
  activeStep = 0,
  onSectionClick = () => {},
  collapsed = false,
  onCollapseToggle = () => {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userTypeId, setUserTypeId] = useState(null);

  // Get user type
  useEffect(() => {
    const userType = localStorage.getItem("userTypeId") || "8";
    setUserTypeId(userType);
  }, []);

  const MenuItems = [
    {
      title: "Group Applications",
      icon: AssignmentIcon,
      path: "/main/director/dashboard",
      completed: false,
      id: "submitted-applications",
    },
    {
      title: "Laboratory Search",
      icon: SearchIcon,
      path: "/main/laboratory-search",
      completed: false,
      id: "laboratory-search",
    },
    {
      title: "Search Scope Master",
      icon: ScienceIcon,
      path: "/main/view-scope-master",
      completed: false,
      id: "view-scope-master",
    },
    {
      title: "Accounts Section",
      icon: AccountBalanceWalletIcon,
      path: "/main/director/outstanding-ledger",
      completed: false,
      id: "outstanding-ledger",
    },
  ];

  const handleSectionClick = (item, index) => {
    console.log("Sidebar - navigating to:", item.path);

    // Check if it's an external URL
    // Open Laboratory Search in new tab
    if (item.id === "laboratory-search" || item.id === 'view-scope-master') {
      window.open(item.path, "_blank");
    }
    // External URLs
    else if (
      item.path.startsWith("http://") ||
      item.path.startsWith("https://")
    ) {
      window.location.href = item.path;
    } else {
      navigate(item.path);
    }

    // Close mobile drawer if open
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transition: "all 0.3s ease-in-out",
          }}
        >
          {!collapsed ? (
            <Box
              sx={{
                opacity: collapsed ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
                flexGrow: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#00024c",
                  transition: "all 0.3s ease-in-out",
                }}
              >
                Director Dashboard
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transition: "all 0.3s ease-in-out",
              }}
            >
              {/* Icon for collapsed state */}
            </Box>
          )}

          {/* Collapse/Expand Button for Desktop */}
          {!isMobile && (
            <IconButton
              onClick={onCollapseToggle}
              sx={{
                color: "#fafafaff",
                backgroundColor: "#1959b9ff",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: "#1959b934",
                  color: "#ffffffff",
                },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}

          {/* Close Button for Mobile */}
          {isMobile && (
            <IconButton onClick={onClose} sx={{ color: "#00024c" }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Navigation Items */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          borderBottom: "1px solid #e0e0e0",
          flexGrow: 1,
          overflowY: "auto",
          transition: "padding 0.3s ease-in-out",
        }}
      >
        <List sx={{ p: 0 }}>
          {MenuItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            const listItemContent = (
              <ListItemButton
                onClick={() => handleSectionClick(item, index)}
                selected={isActive}
                sx={{
                  borderRadius: 1,
                  justifyContent: collapsed ? "center" : "flex-start",
                  px: collapsed ? 1 : 2,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "translateX(2px)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    "&:hover": {
                      backgroundColor: "#bbdefb",
                    },
                  },
                  py: 1,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? "unset" : 35,
                    justifyContent: "center",
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  <item.icon
                    sx={{
                      fontSize: collapsed ? 24 : 18,
                      color: item.completed
                        ? "#4caf50"
                        : isActive
                          ? "#1976d2"
                          : "#666",
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        opacity: collapsed ? 0 : 1,
                        transition: "opacity 0.3s ease-in-out",
                        "& .MuiListItemText-primary": {
                          fontSize: "0.8rem",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#1976d2" : "text.primary",
                          transition: "all 0.3s ease-in-out",
                        },
                      }}
                    />
                    {item.completed && (
                      <VerifiedIcon
                        sx={{
                          color: "#4caf50",
                          fontSize: 16,
                          opacity: collapsed ? 0 : 1,
                          transition: "opacity 0.3s ease-in-out",
                        }}
                      />
                    )}
                  </>
                )}
              </ListItemButton>
            );

            return (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                {collapsed ? (
                  <Tooltip title={item.title} placement="right" arrow>
                    {listItemContent}
                  </Tooltip>
                ) : (
                  listItemContent
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
    </>
  );
};

export default Sidebar;
